// Relative, not the barrel: the early binding imports this module, and the barrel would pull React in ahead of its listener.
// Minting goes through randomUUID, not crypto.randomUUID, because getSessionId runs in a useState initializer where a throw takes down the React tree.
import { randomUUID } from "../random-uuid";
import { resolveLocalStorage, type StorageLike } from "./storage";

// One id per browser forever, NOT per session: production data showed one sessionId spanning 8.6 days. Group per-visit analytics by visitId (./visit-id.ts).
export const SESSION_ID_KEY_SUFFIX = "logging.sessionId.v1";

export interface SessionIdSource {
  readonly key: string;
  readonly getSessionId: (storage?: StorageLike | null) => string;
  // Test-only: vitest shares one module instance per file, so cases would otherwise depend on order.
  readonly resetMintedSessionId: () => void;
}

export function createSessionIdSource(key: string): SessionIdSource {
  // Without this, blocked storage (Chromium with cookies blocked) mints a fresh id per call, so one visit would report many sessionIds.
  let mintedForThisLoad: string | null = null;

  function mintSessionId(): string {
    mintedForThisLoad ??= randomUUID();
    return mintedForThisLoad;
  }

  function getSessionId(
    storage: StorageLike | null = resolveLocalStorage(),
  ): string {
    if (storage === null) return mintSessionId();

    let existing: string | null;
    try {
      existing = storage.getItem(key);
    } catch {
      existing = null;
    }
    if (existing) return existing;

    const minted = mintSessionId();
    try {
      storage.setItem(key, minted);
    } catch {
      // Quota or private mode: the id still works for this page load.
    }
    return minted;
  }

  return {
    key,
    getSessionId,
    resetMintedSessionId: () => {
      mintedForThisLoad = null;
    },
  };
}
